import { Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LANDING } from '@/constants/testIds';
import { BUSINESS, SECTORS } from '@/data/business';

const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.5 6.7a6.6 6.6 0 01-3.9-1.2v9.6a5.7 5.7 0 11-5-5.6v3a2.7 2.7 0 102 2.6V2h3a3.6 3.6 0 00.6 2 3.6 3.6 0 003.3 1.8z" />
    </svg>
);

export const Footer = () => {
    return (
        <footer data-testid={LANDING.footer} className="relative border-t border-white/10 bg-[#070707]">
            <div className="h-1.5 hazard-stripes-thin opacity-70" />

            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 sm:py-20">
                <div className="grid lg:grid-cols-[1.4fr,1fr,1fr,1fr] gap-10">
                    {/* Brand block */}
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 bg-[#FFD500] flex items-center justify-center corner-cut">
                                <span className="font-stencil text-black text-2xl leading-none">M</span>
                            </div>
                            <div>
                                <div className="font-display text-2xl uppercase tracking-wider text-white">
                                    MANU<span className="text-[#FFD500]">VET</span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
                                    Spécialiste des vêtements de travail
                                </div>
                            </div>
                        </div>

                        <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-md">
                            Boutique indépendante à Aulnay-sous-Bois (93) depuis 20 ans. Vêtements de travail,
                            chaussures de sécurité, gants, casques, lunettes et flocage personnalisé pour pros et particuliers.
                        </p>

                        <div className="mt-6 flex flex-wrap gap-3 text-xs">
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD500] hover:text-[#FFD500] transition-colors text-white/70 uppercase tracking-wider font-semibold"
                            >
                                <Phone className="h-3.5 w-3.5" />
                                {BUSINESS.phone}
                            </a>
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD500] hover:text-[#FFD500] transition-colors text-white/70 uppercase tracking-wider font-semibold normal-case"
                            >
                                <Mail className="h-3.5 w-3.5" />
                                <span className="lowercase tracking-normal">{BUSINESS.email}</span>
                            </a>
                            <a
                                href={BUSINESS.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD500] hover:text-[#FFD500] transition-colors text-white/70 uppercase tracking-wider font-semibold"
                                aria-label="Instagram MANU VET"
                                data-testid="landing-footer-instagram"
                            >
                                <Instagram className="h-3.5 w-3.5" />
                                Instagram
                            </a>
                            <a
                                href={BUSINESS.facebookUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD500] hover:text-[#FFD500] transition-colors text-white/70 uppercase tracking-wider font-semibold"
                                aria-label="Facebook MANU VET"
                                data-testid="landing-footer-facebook"
                            >
                                <Facebook className="h-3.5 w-3.5" />
                                Facebook
                            </a>
                            <a
                                href={BUSINESS.tiktokUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FFD500] hover:text-[#FFD500] transition-colors text-white/70 uppercase tracking-wider font-semibold"
                                aria-label="TikTok MANU VET"
                                data-testid="landing-footer-tiktok"
                            >
                                <TikTokIcon className="h-3.5 w-3.5" />
                                TikTok
                            </a>
                        </div>
                    </div>

                    {/* Adresse */}
                    <div>
                        <h4 className="font-display uppercase text-sm text-[#FFD500] tracking-widest mb-4">
                            Boutique
                        </h4>
                        <address className="not-italic text-sm text-white/65 leading-relaxed">
                            <strong className="text-white">MANU VET</strong>
                            <br />
                            {BUSINESS.address}
                            <br />
                            {BUSINESS.postalCode} {BUSINESS.city}
                            <br />
                            {BUSINESS.department}
                        </address>
                        <a
                            href={BUSINESS.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/65 hover:text-[#FFD500] transition-colors"
                        >
                            <MapPin className="h-3 w-3" /> Itinéraire
                        </a>
                    </div>

                    {/* Horaires */}
                    <div>
                        <h4 className="font-display uppercase text-sm text-[#FFD500] tracking-widest mb-4">
                            Horaires
                        </h4>
                        <ul className="space-y-2 text-sm text-white/65">
                            {BUSINESS.openingHours.map((h) => (
                                <li key={h.day}>
                                    <div className="text-[11px] uppercase tracking-wider text-white/55">{h.day}</div>
                                    <div className="text-white">{h.hours}</div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* SEO sectors */}
                    <div>
                        <h4 className="font-display uppercase text-sm text-[#FFD500] tracking-widest mb-4">
                            Secteurs équipés
                        </h4>
                        <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-white/70">
                            {SECTORS.map((s) => (
                                <li key={s} className="hover:text-[#FFD500] transition-colors">{s}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* SEO body */}
                <div className="mt-14 pt-8 border-t border-white/5">
                    <p className="text-[11px] text-white/30 leading-relaxed max-w-5xl">
                        <strong className="text-white/65">MANU VET</strong> — vêtements de travail et EPI à
                        Aulnay-sous-Bois (93600), Seine-Saint-Denis, Île-de-France. Boutique spécialisée dans
                        l'équipement de protection individuelle pour les professionnels du BTP, de l'aéroportuaire
                        (Roissy CDG, Le Bourget), du médical, de la cuisine, de l'automobile, de la défense, de la
                        propreté, de la sécurité incendie, du levage, du transport et de la logistique. Marques
                        Blaklader, Utility Diadora, Safety Jogger, Lafont, Delta Plus, Cepovett, Jallatte, U-Power, Parade, LMA, Singer Safety, Northways, Paper. Services de flocage et broderie
                        sur place. Livraison dans toute l'Île-de-France (Paris 75, 77, 78, 91, 92, 93, 94, 95).
                    </p>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[11px] uppercase tracking-[0.22em] text-white/30">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <span>© {new Date().getFullYear()} MANU VET · Tous droits réservés</span>
                        <span className="hidden sm:inline text-white/15">/</span>
                        <Link
                            to="/mentions-legales"
                            data-testid="footer-legal-mentions"
                            className="hover:text-[#FFD500] transition-colors"
                        >
                            Mentions légales
                        </Link>
                        <span className="text-white/15">·</span>
                        <Link
                            to="/politique-de-confidentialite"
                            data-testid="footer-legal-privacy"
                            className="hover:text-[#FFD500] transition-colors"
                        >
                            Confidentialité
                        </Link>
                    </div>
                    <a
                        href="https://lagence-frappante.fr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 text-white/40 hover:text-[#FFD500] transition-colors"
                    >
                        <span>Créé par</span>
                        <span className="font-display tracking-[0.18em] text-white/70 group-hover:text-[#FFD500] transition-colors">
                            L'AGENCE FRAPPANTE
                        </span>
                        <span className="text-white/30">— lagence-frappante.fr</span>
                    </a>
                </div>
            </div>
        </footer>
    );
};
